// emailApp.js — Application Courriels (type Thunderbird)
import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Drawer, Tabs, Tab, Box, Switch, useMediaQuery, Select, MenuItem, Button, List, ListItem, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert, FormControlLabel, Checkbox, CircularProgress, Typography, IconButton } from '@mui/material';
import { backendUrl, API_KEYS } from '../config';
import axios from 'axios';
import InboxIcon from '@mui/icons-material/Inbox';
import SendIcon from '@mui/icons-material/Send';
import DraftsIcon from '@mui/icons-material/Drafts';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EmailIcon from '@mui/icons-material/Email';
import EditIcon from '@mui/icons-material/Edit';
import DesktopWindow from '../components/DesktopWindow';

const LANGS = {
  fr: {
    title: 'Courriels',
    manageAccounts: 'Gérer les comptes',
    addAccount: 'Ajouter un compte',
    configured: 'Comptes configurés :',
    noAccount: 'Aucun compte configuré',
    label: 'Nom du compte',
    email: 'Adresse courriel',
    imapHost: 'Serveur IMAP',
    imapPort: 'Port IMAP',
    smtpHost: 'Serveur SMTP',
    smtpPort: 'Port SMTP',
    username: 'Nom d\'utilisateur',
    password: 'Mot de passe',
    cancel: 'Annuler',
    add: 'Ajouter',
    delete: 'Supprimer',
    successAdd: 'Compte ajouté avec succès',
    successDelete: 'Compte supprimé',
    error: 'Erreur lors de la requête',
  },
  en: {
    title: 'Emails',
    manageAccounts: 'Manage accounts',
    addAccount: 'Add account',
    configured: 'Configured accounts:',
    noAccount: 'No account configured',
    label: 'Account name',
    email: 'Email address',
    imapHost: 'IMAP server',
    imapPort: 'IMAP port',
    smtpHost: 'SMTP server',
    smtpPort: 'SMTP port',
    username: 'Username',
    password: 'Password',
    cancel: 'Cancel',
    add: 'Add',
    delete: 'Delete',
    successAdd: 'Account added successfully',
    successDelete: 'Account deleted',
    error: 'Request error',
  }
};

const API_BASE = backendUrl;
const API_KEY = API_KEYS.key;
const API_SECRET = API_KEYS.secret;

export default function EmailApp(props) {
  // Thème clair/sombre
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const [darkMode, setDarkMode] = useState(() => {
    const saved = window.localStorage.getItem('mail_theme');
    return saved ? saved === 'dark' : prefersDarkMode;
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [tab, setTab] = useState(0);

  // Ajout des handlers locaux si non fournis
  const handleMinimize = props.onMinimize || (() => alert('Minimiser (démo)'));
  const handleFullscreen = props.onFullscreen || (() => alert('Plein écran (démo)'));
  const handleClose = props.onClose || (() => alert('Fermer (démo)'));

  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: { main: '#1976d2' },
      secondary: { main: '#f50057' },
      background: { default: darkMode ? '#181a1b' : '#f5f5f5' }
    },
  });

  // Gestion du thème utilisateur
  const handleThemeSwitch = () => {
    setDarkMode(m => {
      window.localStorage.setItem('mail_theme', !m ? 'dark' : 'light');
      return !m;
    });
  };

  // Onglets du panneau latéral
  const folders = [
    { icon: <InboxIcon />, label: 'Boîte de réception', aria: 'inbox' },
    { icon: <SendIcon />, label: 'Envoyés', aria: 'sent' },
    { icon: <DraftsIcon />, label: 'Brouillons', aria: 'drafts' },
    { icon: <DeleteIcon />, label: 'Corbeille', aria: 'trash' },
    { icon: <EditIcon />, label: 'Nouveau message', aria: 'compose' },
    { icon: <AccountCircleIcon />, label: 'Comptes', aria: 'accounts' },
    { icon: <SettingsIcon />, label: 'Paramètres', aria: 'settings' }
  ];

  // LOGIQUE EXISTANTE (comptes email, etc.)
  const [lang, setLang] = useState('fr');
  const t = LANGS[lang];
  const [accounts, setAccounts] = useState([]);
  const [notif, setNotif] = useState({ open: false, msg: '', type: 'success' });
  const [error, setError] = useState('');
  const [emails, setEmails] = useState([]);
  const [loadingInbox, setLoadingInbox] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState(null);

  // Définition des dossiers/folders associés aux tabs
  const folderForTab = (tabIdx) => {
    if (tabIdx === 0) return 'INBOX';
    if (tabIdx === 1) return 'Sent';
    if (tabIdx === 2) return 'Drafts';
    if (tabIdx === 3) return 'Trash';
    if (tabIdx === 4) return 'COMPOSE';
    return 'INBOX';
  };

  // Ajout état pour le dossier sélectionné
  const [selectedFolder, setSelectedFolder] = useState(folderForTab(0));

  // Synchronise le dossier sélectionné avec l'onglet
  useEffect(() => {
    setSelectedFolder(folderForTab(tab));
  }, [tab]);

  // Ajout état pour l'email sélectionné
  const [selectedEmail, setSelectedEmail] = useState(null);

  // Fonction utilitaire pour les headers d'authentification
  function getAuthHeaders() {
    const token = localStorage.getItem('token');
    const apiKey = process.env.REACT_APP_API_KEY;
    const apiSecret = process.env.REACT_APP_API_SECRET;
    return {
      ...(token && { 'Authorization': 'Bearer ' + token }),
      ...(apiKey && { 'x-api-key': apiKey }),
      ...(apiSecret && { 'x-api-secret': apiSecret })
    };
  }

  function handleDialogClose() {
    setAccountDialogOpen(false);
    setAccountForm({
      label: '',
      email: '',
      imap_host: '',
      imap_port: 993,
      smtp_host: '',
      smtp_port: 465,
      username: '',
      password: '',
      use_ssl: true
    });
  }

  function handleAccountFormChange(e) {
    const { name, value, type, checked } = e.target;
    setAccountForm(f => ({
      ...f,
      [name]: type === 'checkbox' ? checked : value
    }));
  }

  function handleAddAccount() {
    axios.post(`${API_BASE}/email/accounts`, accountForm, { headers: getAuthHeaders() })
      .then(() => {
        setAccountDialogOpen(false);
        setAccountForm({ label: '', email: '', imap_host: '', imap_port: 993, smtp_host: '', smtp_port: 465, username: '', password: '', use_ssl: true });
        reloadAccounts && reloadAccounts();
      })
      .catch(() => setError("Erreur lors de l'ajout du compte"));
  }

  function handleEditAccount() {
    // Il faut un id pour éditer le compte (on suppose que l'email est unique ou qu'il y a un champ id dans accountForm)
    const id = accounts.find(acc => acc.email === accountForm.email)?.id;
    if (!id) {
      setError("Impossible de trouver le compte à éditer");
      return;
    }
    axios.put(`${API_BASE}/email/accounts/${id}`, accountForm, { headers: getAuthHeaders() })
      .then(() => {
        setAccountDialogOpen(false);
        setAccountForm({ label: '', email: '', imap_host: '', imap_port: 993, smtp_host: '', smtp_port: 465, username: '', password: '', use_ssl: true });
        reloadAccounts && reloadAccounts();
      })
      .catch(() => setError("Erreur lors de la modification du compte"));
  }

  function reloadAccounts() {
    axios.get(`${API_BASE}/email/accounts`, { headers: getAuthHeaders() })
      .then(res => setAccounts(res.data))
      .catch(() => setError("Erreur lors du chargement des comptes"));
  }

  function handleDeleteAccount(id) {
    axios.delete(`${API_BASE}/email/accounts/${id}`, { headers: getAuthHeaders() })
      .then(() => reloadAccounts())
      .catch(() => setError("Erreur lors de la suppression du compte"));
  }

  useEffect(() => {
    if (!selectedAccountId && accounts.length > 0) {
      setSelectedAccountId(accounts[0].id);
    }
  }, [accounts, selectedAccountId]);

  useEffect(() => {
    reloadAccounts();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!selectedAccountId) return;
    if (selectedFolder === 'COMPOSE') return; // Ne pas charger d'emails pour l'onglet composer
    setLoadingInbox(true);
    setError('');
    let url = '';
    if (selectedFolder === 'INBOX') url = `${API_BASE}/email/inbox/${selectedAccountId}`;
    else if (selectedFolder === 'Sent') url = `${API_BASE}/email/sent/${selectedAccountId}`;
    else if (selectedFolder === 'Drafts') url = `${API_BASE}/email/drafts/${selectedAccountId}`;
    else url = `${API_BASE}/email/folder/${selectedAccountId}?folder=${selectedFolder}`;
    axios.get(url, { headers: getAuthHeaders() })
      .then(res => setEmails(res.data))
      .catch(e => setError('Erreur lors du chargement des emails'))
      .finally(() => setLoadingInbox(false));
  }, [selectedAccountId, selectedFolder]);

  function handleReadEmail(msgId) {
    setLoadingInbox(true);
    setError('');
    axios.get(`${API_BASE}/email/message/${selectedAccountId}/${msgId}?folder=${selectedFolder}`, { headers: getAuthHeaders() })
      .then(res => setSelectedEmail(res.data))
      .catch(e => setError('Erreur lors de la lecture du message'))
      .finally(() => setLoadingInbox(false));
  };

  // Restauration : ouvrir une fenêtre globale via window.openComposeOnDesktop
  function openComposeWindow() {
    if (window.openComposeOnDesktop) {
      window.openComposeOnDesktop();
    } else {
      alert('La fonction d’ouverture de fenêtre composeur est indisponible.');
    }
  }

  function handleRestoreEmail(msgId) {
    setLoadingInbox(true);
    setError('');
    axios.post(`${API_BASE}/email/restore/${selectedAccountId}/${msgId}?folder=Trash`, {}, { headers: getAuthHeaders() })
      .then(() => {
        setSelectedEmail(null);
        reloadAccounts && reloadAccounts();
      })
      .catch(() => setError('Erreur lors de la restauration du message'))
      .finally(() => setLoadingInbox(false));
  };

  function handleDeleteForever(msgId) {
    setLoadingInbox(true);
    setError('');
    axios.delete(`${API_BASE}/email/delete/${selectedAccountId}/${msgId}?folder=Trash`, { headers: getAuthHeaders() })
      .then(() => {
        setSelectedEmail(null);
        reloadAccounts && reloadAccounts();
      })
      .catch(() => setError('Erreur lors de la suppression définitive'))
      .finally(() => setLoadingInbox(false));
  };

  // Ajout de l'état pour la gestion du dialog de comptes
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  // Ajout de l'état pour le formulaire de compte
  const [accountForm, setAccountForm] = useState({
    label: '',
    email: '',
    imap_host: '',
    imap_port: 993,
    smtp_host: '',
    smtp_port: 465,
    username: '',
    password: '',
    use_ssl: true
  });

  // Ajout de l'état pour le mode édition
  const [isEditMode, setIsEditMode] = useState(false);

  // Fonction pour ouvrir le dialog d'édition de compte
  function openEditAccount(account) {
    setIsEditMode(true);
    setAccountDialogOpen(true);
    setAccountForm({
      label: account.label || '',
      email: account.email || '',
      imap_host: account.imap_host || '',
      imap_port: account.imap_port || 993,
      smtp_host: account.smtp_host || '',
      smtp_port: account.smtp_port || 465,
      username: account.username || '',
      password: '', // On ne pré-remplit pas le mot de passe pour la sécurité
      use_ssl: account.use_ssl !== undefined ? account.use_ssl : true
    });
  }

  const folderTabs = (
    <Tabs value={selectedFolder} onChange={(e, v) => setSelectedFolder(v)}>
      <Tab value="INBOX" label="Réception" />
      <Tab value="Sent" label="Envoyés" />
      <Tab value="Drafts" label="Brouillons" />
    </Tabs>
  );

  const emailList = (
    <Box>
      {loadingInbox ? <CircularProgress /> :
        emails.length === 0 ? <Typography>Aucun message</Typography> :
        emails.map(email => (
          <Box key={email.id} onClick={() => handleReadEmail(email.id)} sx={{ cursor: 'pointer', borderBottom: '1px solid #eee', py: 1 }}>
            <Typography variant="subtitle2">{email.subject || '(Sans sujet)'}</Typography>
            <Typography variant="body2">De: {email.from} - {email.date}</Typography>
          </Box>
        ))}
    </Box>
  );

  const emailDetail = selectedEmail && (
    <Box sx={{ mt: 2, p: 2, border: '1px solid #ccc', borderRadius: 2 }}>
      <Typography variant="h6">{selectedEmail.subject}</Typography>
      <Typography variant="body2">De: {selectedEmail.from}</Typography>
      <Typography variant="body2">À: {selectedEmail.to}</Typography>
      <Typography variant="body2">Date: {selectedEmail.date}</Typography>
      <Box sx={{ mt: 2, whiteSpace: 'pre-wrap', overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>{selectedEmail.body}</Box>
      <Button sx={{ mt: 2 }} onClick={() => setSelectedEmail(null)}>Retour</Button>
    </Box>
  );

  // Ajout : état pour stocker les données du mail à composer
  const [composeData, setComposeData] = useState({ to: '', subject: '', body: '', cc: '', bcc: '' });
  const [sending, setSending] = useState(false);
  const [composeError, setComposeError] = useState('');

  function handleSendCompose() {
    setSending(true);
    setComposeError('');
    axios.post(`${API_BASE}/email/send`, {
      account_id: selectedAccountId,
      ...composeData
    }, { headers: getAuthHeaders() })
      .then(() => {
        setTab(0); // retour à la boîte de réception après envoi
        setComposeData({ to: '', subject: '', body: '', cc: '', bcc: '' });
      })
      .catch(() => setComposeError("Erreur lors de l'envoi du courriel"))
      .finally(() => setSending(false));
  }

  function handleOpenComposeTab() {
    setTab(4); // index du nouvel onglet
    setComposeData({ to: '', subject: '', body: '', cc: '', bcc: '' });
    setComposeError('');
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* SUPPRIMER LA BARRE BLEUE DE L'APP : Retirer complètement l'AppBar et Toolbar ici */}
      {/* Les boutons de contrôle de fenêtre sont déjà gérés par DesktopWindow, donc on retire toute AppBar locale */}
      <Box sx={{ display: 'flex', minHeight: '100vh', pt: 8, overflowY: 'auto' }}>
        <Box sx={{ width: 240, display: { xs: 'none', md: 'block' } }}>
          <Box role="presentation" sx={{ width: 240 }}>
            <Button variant="contained" color="primary" fullWidth sx={{ mt: 2, mb: 2 }} onClick={handleOpenComposeTab}>
              Nouveau message
            </Button>
            <Tabs
              orientation="vertical"
              value={tab}
              onChange={(e, newValue) => {
                if (newValue === 4) {
                  handleOpenComposeTab();
                } else {
                  setTab(newValue);
                }
              }}
              sx={{ borderRight: 1, borderColor: 'divider', minHeight: '100vh' }}
            >
              {folders.map((folder, idx) => (
                <Tab
                  key={folder.label}
                  icon={folder.icon}
                  label={folder.label}
                  sx={{ alignItems: 'flex-start' }}
                />
              ))}
            </Tabs>
          </Box>
        </Box>
        <Box sx={{ flexGrow: 1, p: { xs: 1, md: 3 } }}>
          {tab === 0 && (
            <Box>
              <Typography variant="h6">{t.title} — Boîte de réception</Typography>
              {accounts.length > 1 && (
                <Select
                  size="small"
                  value={selectedAccountId || (accounts[0] && accounts[0].id) || ''}
                  onChange={e => setSelectedAccountId(e.target.value)}
                  sx={{ mb: 2, minWidth: 200 }}
                >
                  {accounts.map(acc => (
                    <MenuItem key={acc.id} value={acc.id}>{acc.label} — {acc.email}</MenuItem>
                  ))}
                </Select>
              )}
              {folderTabs}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 300 }}>
                  {emailList}
                </Box>
                <Box sx={{ flex: 2, minWidth: 400, overflowY: 'auto', maxHeight: 'calc(100vh - 120px)' }}>
                  {selectedEmail ? (
                    <Box>
                      {emailDetail}
                      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                        <Button variant="outlined" color="primary" onClick={() => handleRestoreEmail(selectedEmail.id)}>
                          Restaurer
                        </Button>
                        <Button variant="outlined" color="error" onClick={() => handleDeleteForever(selectedEmail.id)}>
                          Supprimer définitivement
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 4 }}>
                      Sélectionnez un courriel pour l'afficher ici.
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
          {tab === 1 && (
            <Box>
              <Typography variant="h6">{t.title} — Envoyés</Typography>
              {accounts.length > 1 && (
                <Select
                  size="small"
                  value={selectedAccountId || (accounts[0] && accounts[0].id) || ''}
                  onChange={e => setSelectedAccountId(e.target.value)}
                  sx={{ mb: 2, minWidth: 200 }}
                >
                  {accounts.map(acc => (
                    <MenuItem key={acc.id} value={acc.id}>{acc.label} — {acc.email}</MenuItem>
                  ))}
                </Select>
              )}
              {folderTabs}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 300 }}>
                  {emailList}
                </Box>
                <Box sx={{ flex: 2, minWidth: 400 }}>
                  {selectedEmail ? emailDetail : (
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 4 }}>
                      Sélectionnez un courriel pour l'afficher ici.
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
          {tab === 2 && (
            <Box>
              <Typography variant="h6">{t.title} — Brouillons</Typography>
              {accounts.length > 1 && (
                <Select
                  size="small"
                  value={selectedAccountId || (accounts[0] && accounts[0].id) || ''}
                  onChange={e => setSelectedAccountId(e.target.value)}
                  sx={{ mb: 2, minWidth: 200 }}
                >
                  {accounts.map(acc => (
                    <MenuItem key={acc.id} value={acc.id}>{acc.label} — {acc.email}</MenuItem>
                  ))}
                </Select>
              )}
              {folderTabs}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 300 }}>
                  {emailList}
                </Box>
                <Box sx={{ flex: 2, minWidth: 400 }}>
                  {selectedEmail ? emailDetail : (
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 4 }}>
                      Sélectionnez un courriel pour l'afficher ici.
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
          {tab === 3 && (
            <Box>
              <Typography variant="h6">{t.title} — Corbeille</Typography>
              {accounts.length > 1 && (
                <Select
                  size="small"
                  value={selectedAccountId || (accounts[0] && accounts[0].id) || ''}
                  onChange={e => setSelectedAccountId(e.target.value)}
                  sx={{ mb: 2, minWidth: 200 }}
                >
                  {accounts.map(acc => (
                    <MenuItem key={acc.id} value={acc.id}>{acc.label} — {acc.email}</MenuItem>
                  ))}
                </Select>
              )}
              {folderTabs}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 300 }}>
                  {emailList}
                </Box>
                <Box sx={{ flex: 2, minWidth: 400 }}>
                  {selectedEmail ? (
                    <Box>
                      {emailDetail}
                      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                        <Button variant="outlined" color="primary" onClick={() => handleRestoreEmail(selectedEmail.id)}>
                          Restaurer
                        </Button>
                        <Button variant="outlined" color="error" onClick={() => handleDeleteForever(selectedEmail.id)}>
                          Supprimer définitivement
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 4 }}>
                      Sélectionnez un courriel pour l'afficher ici.
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
          {tab === 4 && (
            <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 4, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 2 }}>
              <Typography variant="h6" gutterBottom>Composer un message</Typography>
              <TextField fullWidth label="À" value={composeData.to} onChange={e => setComposeData({ ...composeData, to: e.target.value })} sx={{ mb: 2 }} />
              <TextField fullWidth label="Sujet" value={composeData.subject} onChange={e => setComposeData({ ...composeData, subject: e.target.value })} sx={{ mb: 2 }} />
              <TextField fullWidth label="Corps" multiline minRows={4} value={composeData.body} onChange={e => setComposeData({ ...composeData, body: e.target.value })} sx={{ mb: 2 }} />
              <TextField fullWidth label="Cc" value={composeData.cc} onChange={e => setComposeData({ ...composeData, cc: e.target.value })} sx={{ mb: 2 }} />
              <TextField fullWidth label="Bcc" value={composeData.bcc} onChange={e => setComposeData({ ...composeData, bcc: e.target.value })} sx={{ mb: 2 }} />
              {composeError && <div style={{ color: 'red', marginBottom: 8 }}>{composeError}</div>}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <Button onClick={() => setTab(0)}>Annuler</Button>
                <Button variant="contained" color="primary" onClick={handleSendCompose} disabled={sending}>{sending ? 'Envoi...' : 'Envoyer'}</Button>
              </Box>
            </Box>
          )}
          {tab === 5 && (
  <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 2, p: 3 }}>
    <Typography variant="h6" gutterBottom>{t.manageAccounts}</Typography>
    <Button
      variant="contained"
      color="primary"
      sx={{ mb: 2 }}
      onClick={() => {
        setIsEditMode(false);
        setAccountDialogOpen(true);
        setAccountForm({ label: '', email: '', imap_host: '', imap_port: 993, smtp_host: '', smtp_port: 465, username: '', password: '', use_ssl: true });
      }}
    >
      {t.addAccount}
    </Button>
    {accounts.length === 0 ? (
      <Typography color="textSecondary">{t.noAccount}</Typography>
    ) : (
      <List>
        {accounts.map(acc => (
          <ListItem key={acc.id} sx={{ display: 'flex', alignItems: 'center' }}>
            <ListItemText
              primary={`${acc.label} — ${acc.email}`}
              secondary={`IMAP: ${acc.imap_host}:${acc.imap_port} | SMTP: ${acc.smtp_host}:${acc.smtp_port}`}
            />
            <IconButton edge="end" aria-label="edit" onClick={() => openEditAccount(acc)}>
              <EditIcon />
            </IconButton>
            <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteAccount(acc.id)}>
              <DeleteIcon />
            </IconButton>
          </ListItem>
        ))}
      </List>
    )}
    {/* Dialog d'ajout/édition de compte */}
    <Dialog open={accountDialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditMode ? t.label + ' — ' + t.edit : t.addAccount}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin="normal"
          label={t.label}
          name="label"
          value={accountForm.label}
          onChange={handleAccountFormChange}
        />
        <TextField
          fullWidth
          margin="normal"
          label={t.email}
          name="email"
          value={accountForm.email}
          onChange={handleAccountFormChange}
        />
        <TextField
          fullWidth
          margin="normal"
          label={t.imapHost}
          name="imap_host"
          value={accountForm.imap_host}
          onChange={handleAccountFormChange}
        />
        <TextField
          fullWidth
          margin="normal"
          label={t.imapPort}
          name="imap_port"
          type="number"
          value={accountForm.imap_port}
          onChange={handleAccountFormChange}
        />
        <TextField
          fullWidth
          margin="normal"
          label={t.smtpHost}
          name="smtp_host"
          value={accountForm.smtp_host}
          onChange={handleAccountFormChange}
        />
        <TextField
          fullWidth
          margin="normal"
          label={t.smtpPort}
          name="smtp_port"
          type="number"
          value={accountForm.smtp_port}
          onChange={handleAccountFormChange}
        />
        <TextField
          fullWidth
          margin="normal"
          label={t.username}
          name="username"
          value={accountForm.username}
          onChange={handleAccountFormChange}
        />
        <TextField
          fullWidth
          margin="normal"
          label={t.password}
          name="password"
          type="password"
          value={accountForm.password}
          onChange={handleAccountFormChange}
        />
        <FormControlLabel
          control={<Checkbox checked={accountForm.use_ssl} onChange={handleAccountFormChange} name="use_ssl" />}
          label="SSL"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose}>{t.cancel}</Button>
        <Button variant="contained" color="primary" onClick={isEditMode ? handleEditAccount : handleAddAccount}>
          {isEditMode ? t.edit : t.add}
        </Button>
      </DialogActions>
    </Dialog>
  </Box>
)}
        </Box>
      </Box>
    </ThemeProvider>
  );
}
